import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse, ApiErrorResponse } from "../../../types/api";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from "../../../hooks/useCommonOptions";
import SubmissionReportDialog, {
  SubmissionReport,
  BatchError,
} from "../components/SubmissionReportDialog";
import { useDynamicMappings } from "../../../hooks/useDynamicMappings";
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {
  BatchRecordError,
  MappingSubmitResponse,
  UpdateIfExistsMap,
} from "../../,,/../../interfaces/CommonInterface";
import UploadData from "../components/UploadData";
import {
  scrollToErrorRow,
  validateMapping,
} from "../../../hooks/useCommonValidate";
import DataSourceTable from "../components/DataSourceTable";
import { useAutoMapping } from "../../../hooks/useAutoMapping";

interface EconomicContact {
  index?: number;
  address: string;
  city: string;
  corporateIdentificationNumber: string;
  pNumber: string;
  country: string;

  currency: string;

  customerNumber: string;
  ean: string;
  email: string;
  name: string;
  publicEntryNumber: string;
  telephoneAndFaxNumber: string;
  mobilePhone: string;
  vatNumber: string;
  website: string;
  zip: string;
  attention?: {
    customerContactNumber: string;
  };
  customerContact?: {
    customerContactNumber: string;
  };
  customerGroup?: {
    customerGroupNumber: string;
    originalValue?: string;
  };
  layout?: {
    layoutNumber: string;
    originalValue?: string;
  };
  paymentTerms?: {
    paymentTermsNumber: string;
    originalValue?: string;
  };
  salesPerson?: {
    employeeNumber: string;
  };
  vatZone: {
    vatZoneNumber: string;
    originalValue?: string;
  };
}

interface CustomerPanelProps {}

interface Mapping {
  address: string;
  city: string;
  corporateIdentificationNumber: string;
  pNumber: string;
  country: string;
  currency: string;
  customerNumber: string;
  ean: string;
  email: string;
  name: string;
  publicEntryNumber: string;
  telephoneAndFaxNumber: string;
  mobilePhone: string;
  vatNumber: string;
  website: string;
  zip: string;
  attention_customerContactNumber?: string;
  customerContact_customerContactNumber?: string;

  customerGroup_customerGroupNumber?: string;
  layout_layoutNumber?: string;
  paymentTerms_paymentTermsNumber?: string;
  salesPerson_employeeNumber?: string;
  vatZone_vatZoneNumber: string;
}

interface DefaultValues extends Mapping {}

const CustomerPanel: React.FC<CustomerPanelProps> = () => {
  const [sourceData, setSourceData] = useState<any[][]>([]);
  const [originalSourceData, setOriginalSourceData] = useState<any[][]>([]);
  const [sourceColumns, setSourceColumns] = useState<
    { id: string; label: string }[]
  >([]);
  const [economicContacts, setEconomicContacts] = useState<EconomicContact[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>({
    address: "",
    city: "",
    corporateIdentificationNumber: "",
    pNumber: "",
    country: "",
    currency: "",
    customerNumber: "",
    ean: "",
    email: "",
    name: "",
    publicEntryNumber: "",
    telephoneAndFaxNumber: "",
    mobilePhone: "",
    vatNumber: "",
    website: "",
    zip: "",
    attention_customerContactNumber: "",
    customerContact_customerContactNumber: "",
    customerGroup_customerGroupNumber: "",
    layout_layoutNumber: "",
    paymentTerms_paymentTermsNumber: "",
    salesPerson_employeeNumber: "",
    vatZone_vatZoneNumber: "",
  });
  const [batchSize] = useState(10);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const tableRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>(
    {}
  );
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    address: "",
    city: "",
    corporateIdentificationNumber: "",
    pNumber: "",
    country: "",
    currency: "",
    customerNumber: "",
    ean: "",
    email: "",
    name: "",
    publicEntryNumber: "",
    telephoneAndFaxNumber: "",
    mobilePhone: "",
    vatNumber: "",
    website: "",
    zip: "",
    attention_customerContactNumber: "",
    customerContact_customerContactNumber: "",
    customerGroup_customerGroupNumber: "",
    layout_layoutNumber: "",
    paymentTerms_paymentTermsNumber: "",
    salesPerson_employeeNumber: "",
    vatZone_vatZoneNumber: "",
  });
  const [updateExistingCustomer, setUpdateExistingCustomer] =
    useState<boolean>(false);
  const [requiredFields] = useState<Array<keyof Mapping>>([
    "name",
    "paymentTerms_paymentTermsNumber",
    "vatZone_vatZoneNumber",
    "customerGroup_customerGroupNumber",
    "currency",
  ]);
  const [updateKey, setUpdateKey] = useState<string>("");
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] =
    useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const [skipLines, setSkipLines] = useState<number>(0);

  const columnMappings = {
    name: [
      "name",
      "customer name",
      "company name",
      "company",
      "customer",
      "Navn",
    ],
    address: ["address", "street", "street address", "Adresse1"],
    city: ["city", "town", "ByNavn", "By",'TownCity'],
    zip: ["zip", "postal code", "postcode", "zip code", "PostNr",'Zipcode'],
    country: ["country", "nation", "Land"],
    email: ["email", "e-mail", "mail", "Email"],
    website: ["website", "web", "url", "site", "website", "Hjemmeside"],
    telephoneAndFaxNumber: ["phone", "telephone", "tel", "fax", "TelefonFax",'TelephoneFax'],
    customerNumber: [
      "customer number",
      "customer no",
      "no.",
      "CustomerNo",
      "KundeNr",
    ],
    vatNumber: ["vat", "vat number", "vat no", "tax number",'CVR-nr.','CVR-nr.','CVR-nr','Co. reg. no.','Co. reg. no','Co. reg. no.','Co. reg. no'],
    currency: ["currency", "curr",'Valuta'],
    customerGroup_customerGroupNumber: [
      "group",
      "customer group",
      "KundeGruppe",
    ],
    paymentTerms_paymentTermsNumber: ["payment terms", "terms", "payment",'BetalingsBetingelser','PaymentTerms'],
    vatZone_vatZoneNumber: ["vat zone", "tax zone",'Zone'],
    corporateIdentificationNumber: [
      "corporate id",
      "company id",
      "registration number",
    ],
    // attention_customerContactNumber: ["Attention",'AttentionNo'], 
    ean: ["ean"],
    pNumber: ["p number", "p-number", "pno", "p no", "p-number", "p#", "p #",'PNo','PNr','PNo'],
    publicEntryNumber: ["public entry number", "public entry no", "public entry no.", "public entry no", "public entry no.", "public entry no.", 'PublicEntryNo','PublicEntryNo.','PublicEntryNo','KonteringsNr'],

  };

  const { handleAutoMapFirstRow, autoMappingDone, setAutoMappingDone } =
    useAutoMapping<Mapping>({
      sourceData,
      originalSourceData,
      mapping,
      setMapping,
      setSourceColumns,
      setSuccess,
      setSkipLines,
      onSourceDataUpdate: (newData) => {
        setSourceData(newData);
        setTotalCount(newData.length);
      },
      columnMappings,
    });

  const api = useApi();
  const { currencies, paymentTerms, vatZones, customerGroups } =
    useCommonOptions();
  const { getMappedValue: getDynamicMappedValue } = useDynamicMappings();

  // Add useEffect to handle mapping updates
  useEffect(() => {
    if (autoMappingDone && sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
      setAutoMappingDone(false);
    }
  }, [mapping, autoMappingDone, sourceData]);

  const handleSkipLinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    setSkipLines(value);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapSourceToEconomic = (sourceData: any[][]) => {
    // Skip the specified number of lines
    const dataToProcess = sourceData.slice(skipLines);
    const mappedContacts = dataToProcess.map((row, index) => {
      function getMappedValue(field: string): {
        mapped: string | null;
        original: string | null;
      } {
        const def = getDefaultValue(field as keyof DefaultValues);
        if (def) return { mapped: def, original: null };

        if (field in mapping) {
          const key = mapping[field];
          if (key) {
            // Convert key to column index (assuming key is in format "col0", "col1", etc.)
            const colIndex = parseInt(key.replace("col", ""));
            if (!isNaN(colIndex) && colIndex < row.length) {
              const raw = row[colIndex];
              const mapped = getDynamicMappedValue(field, raw);
              return { mapped, original: raw };
            }
          }
        }
        return { mapped: null, original: null };
      }

      const {
        mapped: paymentTermsNumber,
        original: paymentTermsNumberOriginal,
      } = getMappedValue("paymentTerms_paymentTermsNumber");
      const { mapped: vatZoneNumber, original: vatZoneNumberOriginal } =
        getMappedValue("vatZone_vatZoneNumber");
      const {
        mapped: customerGroupCustomerGroupNumber,
        original: customerGroupCustomerGroupNumberOriginal,
      } = getMappedValue("customerGroup_customerGroupNumber");
      const {
        mapped: layoutLayoutNumber,
        original: layoutLayoutNumberOriginal,
      } = getMappedValue("layout_layoutNumber");

      return {
        index: index + skipLines,
        address: getMappedValue("address").mapped,
        city: getMappedValue("city").mapped,
        corporateIdentificationNumber: getMappedValue(
          "corporateIdentificationNumber"
        ).mapped,
        pNumber: getMappedValue("pNumber").mapped,
        country: getMappedValue("country").mapped,
        currency: getMappedValue("currency").mapped,
        customerNumber: getMappedValue("customerNumber").mapped,
        ean: getMappedValue("ean").mapped,
        email: getMappedValue("email").mapped,
        name: getMappedValue("name").mapped,
        publicEntryNumber: getMappedValue("publicEntryNumber").mapped,
        telephoneAndFaxNumber: getMappedValue("telephoneAndFaxNumber").mapped,
        mobilePhone: getMappedValue("mobilePhone").mapped,
        vatNumber: getMappedValue("vatNumber").mapped,
        website: getMappedValue("website").mapped,
        zip: getMappedValue("zip").mapped,
        attention: {
          customerContactNumber: getMappedValue(
            "attention_customerContactNumber"
          ).mapped,
        },
        customerContact: {
          customerContactNumber: getMappedValue(
            "customerContact_customerContactNumber"
          ).mapped,
        },
        customerGroup: {
          customerGroupNumber: customerGroupCustomerGroupNumber,
          originalValue: customerGroupCustomerGroupNumberOriginal,
        },
        layout: {
          layoutNumber: layoutLayoutNumber,
          originalValue: layoutLayoutNumberOriginal,
        },
        paymentTerms: {
          paymentTermsNumber: paymentTermsNumber,
          originalValue: paymentTermsNumberOriginal,
        },
        salesPerson: {
          employeeNumber: getMappedValue("salesPerson_employeeNumber").mapped,
        },
        vatZone: {
          vatZoneNumber: vatZoneNumber,
          originalValue: vatZoneNumberOriginal,
        },
      };
    });

    setEconomicContacts(mappedContacts);
    setSuccess("Successfully mapped " + mappedContacts.length + " contacts");
  };

  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping(
      mapping,
      defaultValues,
      requiredFields
    );
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    if (updateExistingCustomer && !updateKey) {
      setError("Please select a key for updating existing customers");
      return;
    }

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicContacts.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        // Check if should stop
        if (shouldStop) {
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchContacts = economicContacts.slice(start, end);

        const filteredBatch = batchContacts
          .map((contact) => deepClean(contact))
          .filter((item) => item && Object.keys(item).length > 0);

        const response = await api.post<MappingSubmitResponse>(
          "/main/create-customer",
          {
            mapping,
            contacts: filteredBatch,
            batchInfo: {
              currentBatch: i + 1,
              totalBatches: batches,
            },
            updateIfExists: updateIfExistsMap,
          },
          {
            signal: abortControllerRef.current.signal,
          }
        );

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach((error) => {
            setErrorRecords((prev) => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach((error) => {
          if (typeof error === "string") {
            reportErrors.push(error);
          } else {
            error.errors.forEach((err) => {
              reportErrors.push({
                recordId: error.recordId,
                field: "",
                message: err,
                value: "",
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors,
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error) {
      if (error.status === 500 && error.message === "canceled") {
        setShowReport(true);
        setShouldStop(false);
        // Don't show message here as it's already shown in handleStopSubmit
      } else {
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
    }
  };

  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(
      `Process stopped. Successfully processed ${processedCount} records.`
    );
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };

  return (
    <>
      <UploadData
        onLoading={(loading) => {
          setLoading(loading);
        }}
        onLoadData={(data, columns) => {
          setSourceData(data);
          setOriginalSourceData(data);
          setSourceColumns(columns);
          setTotalCount(data.length);
          mapSourceToEconomic(data.slice(skipLines));
        }}
        onError={(msg) => {
          setError(msg);
        }}
      />
      <MappingDialogs
        mapping={mapping}
        defaultValues={defaultValues}
        type="customer"
        onApplyMapping={(newMap, defaults) => {
          setMapping(newMap);
          setDefaultValues(defaults);
        }}
        onSuccess={(msg) => setSuccess(msg)}
      />

      <SubmitOptions
        mapSourceDataToEconomic={mapSourceToEconomic}
        sourceData={sourceData}
        economicData={economicContacts}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
        skipLines={skipLines}
        handleSkipLinesChange={handleSkipLinesChange}
        onAutoMapFirstRow={handleAutoMapFirstRow}
      />

      <DataSourceTable
        sourceColumns={sourceColumns}
        sourceData={sourceData}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        skipLines={skipLines}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt: 2 }}
      >
        E-conomic Customers
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 440 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic customer table"
        >
          <TableHead>
            <TableRow>
              <MappingCell
                key="customerNumber"
                label="Customer No."
                field="customerNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("customerNumber", value)
                }
              />
              <MappingCell
                key="customerGroup"
                label="Group"
                field="customerGroup_customerGroupNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={customerGroups}
              />

              <MappingCell
                key="name"
                label="Name"
                field="name"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("name", value)
                }
              />

              <MappingCell
                label="Address"
                field="address"
                key="address"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="Zip"
                field="zip"
                key="zip"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="City"
                field="city"
                key="city"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                label="Country"
                field="country"
                key="country"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                // itemOptions={countryOptions}
              />

              <MappingCell
                label="Fax/Phone"
                field="telephoneAndFaxNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("telephoneAndFaxNumber", value)
                }
              />
              <MappingCell
                label="Email"
                field="email"
                key="email"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("email", value)
                }
              />

              <MappingCell
                label="Website"
                field="website"
                key="website"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                key="vatZone"
                label="Vatzone"
                field="vatZone_vatZoneNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={vatZones}
              />

              <MappingCell
                key="currency"
                label="Currency"
                field="currency"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={currencies}
              />

              <MappingCell
                label="Corporate ID"
                field="corporateIdentificationNumber"
                key="corporateIdentificationNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange(
                    "corporateIdentificationNumber",
                    value
                  )
                }
              />

              <MappingCell
                key="paymentTerms"
                label="Payment Terms"
                field="paymentTerms_paymentTermsNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={paymentTerms}
              />
              <MappingCell
                key="ean"
                label="EAN"
                field="ean"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("ean", value)
                }
              />
              <MappingCell
                label="P Number"
                field="pNumber"
                key="pNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="Mobile Phone"
                field="mobilePhone"
                key="mobilePhone"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('mobilePhone', value)}
              />

              <MappingCell
                label="VAT Number"
                field="vatNumber"
                key="vatNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('vatNumber', value)}
              />

              <MappingCell
                label="Public Entry No."
                field="publicEntryNumber"
                key="publicEntryNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('publicEntryNumber', value)}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicContacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact) => {
                  const hasError = errorRecords.has(
                    contact?.index?.toString() || ""
                  );
                  return (
                    <TableRow
                      key={contact.index}
                      id={`row-${contact.index}`}
                      sx={{
                        backgroundColor: hasError
                          ? "rgba(255, 0, 0, 0.1)"
                          : undefined,
                        "&:hover": {
                          backgroundColor: hasError
                            ? "rgba(255, 0, 0, 0.2)"
                            : undefined,
                        },
                      }}
                    >
                      <ValueCell value={contact.customerNumber} />
                      <ValueCell
                        value={contact.customerGroup?.customerGroupNumber}
                        originalValue={contact.customerGroup?.originalValue}
                        options={customerGroups}
                      />
                      <ValueCell value={contact.name} />
                      <ValueCell value={contact.address} />
                      <ValueCell value={contact.zip} />
                      <ValueCell value={contact.city} />

                      <ValueCell value={contact.country} />
                      <ValueCell value={contact.telephoneAndFaxNumber} />
                      <ValueCell value={contact.email} />
                      <ValueCell value={contact.website} />
                      <ValueCell
                        value={contact.vatZone.vatZoneNumber}
                        originalValue={contact.vatZone.originalValue}
                        options={vatZones}
                      />
                      <ValueCell
                        value={contact.currency}
                        options={currencies}
                      />
                      <ValueCell
                        value={contact.corporateIdentificationNumber}
                      />

                      <ValueCell
                        value={contact.paymentTerms?.paymentTermsNumber}
                        originalValue={contact.paymentTerms?.originalValue}
                        options={paymentTerms}
                      />

                      <ValueCell value={contact.ean} />
                      <ValueCell value={contact.pNumber} />
                      <ValueCell value={contact.mobilePhone}/>
                      <ValueCell value={contact.vatNumber}/>
                      <ValueCell value={contact.publicEntryNumber}/>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>

      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={(recordId) => {
          scrollToErrorRow(
            recordId,
            economicContacts,
            rowsPerPage,
            page,
            setPage,
            tableRef
          );
        }}
      />
    </>
  );
};

export default memo(CustomerPanel);
